/**
 * Textarea — Business app multi-line input primitive.
 *
 * Enforces UX Blueprint §9:
 *   - Minimum 3 rows visible on tablet
 *   - Visible focus ring; invalid border state
 *   - Pairs with <Label>, <FieldError> for a11y
 */
import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '../utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full px-biz-content-lg py-biz-content',
        'bg-biz-background text-biz-foreground',
        'border border-biz-input rounded-biz-input',
        'placeholder:text-biz-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:border-biz-primary',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-colors duration-biz-fast resize-y',
        invalid && 'border-biz-danger focus-visible:ring-biz-danger',
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';
